import { z } from 'zod';
import { InstalledModSchema } from './mods.js';
import { SoftwareSpecSchema } from './domain.js';

// 大型パックも Panel のディスクへストリーム保存する。
export const MODPACK_LIMITS = { archiveBytes: 1024 ** 3, expandedBytes: 3 * 1024 ** 3, files: 50000 };
export const MODPACK_ARCHIVE_LIMIT_LABEL =
  MODPACK_LIMITS.archiveBytes >= 1024 ** 3
    ? `${MODPACK_LIMITS.archiveBytes / 1024 ** 3}GB`
    : `${MODPACK_LIMITS.archiveBytes / 1024 ** 2}MB`;
export const PackPathSchema = z
  .string()
  .min(1)
  .max(500)
  .refine(
    (value) =>
      !/[\\\0:]/.test(value) &&
      !value.includes('..') &&
      !value.startsWith('/') &&
      value.split('/').every((part) => part !== '' && part !== '.' && part !== '..' && !/[. ]$/.test(part)),
    '安全な相対パスを指定してください',
  );
export const PackDownloadUrlSchema = z
  .string()
  .url()
  .refine((value) => {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      (!url.port || url.port === '443') &&
      [
        'cdn.modrinth.com',
        'edge.forgecdn.net',
        'mediafilez.forgecdn.net',
        'github.com',
        'raw.githubusercontent.com',
        'gitlab.com',
      ].includes(url.hostname)
    );
  }, 'Modpack のダウンロード先は許可されていません');
const sha1 = z.string().regex(/^[0-9a-f]{40}$/);
const sha512 = z.string().regex(/^[0-9a-f]{128}$/);
const side = z.enum(['required', 'optional', 'unsupported']);
export const PackFileSchema = z.object({
  path: PackPathSchema,
  // CurseForge は sha1 しか出さない。sha512 は Modrinth のときだけ確かめる
  hashes: z.object({ sha1, sha512: sha512.optional() }),
  downloads: z.array(PackDownloadUrlSchema).min(1).max(10),
  fileSize: z.number().int().nonnegative().max(MODPACK_LIMITS.expandedBytes),
  env: z
    .object({ client: side.default('required'), server: side.default('required') })
    .default({ client: 'required', server: 'required' }),
});
export const ImportIndexSchema = z
  .object({
    formatVersion: z.literal(1),
    game: z.literal('minecraft'),
    name: z.string().min(1).max(200),
    versionId: z.string().min(1).max(100),
    dependencies: z.record(z.string(), z.string().min(1).max(100)),
    files: z.array(PackFileSchema).max(MODPACK_LIMITS.files),
  })
  .superRefine((index, ctx) => {
    const paths = index.files.map((file) => file.path.toLowerCase());
    if (new Set(paths).size !== paths.length)
      ctx.addIssue({ code: 'custom', message: 'ファイルのパスが重複しています' });
    if (index.files.reduce((sum, file) => sum + file.fileSize, 0) > 8 * 1024 ** 3)
      ctx.addIssue({ code: 'custom', message: 'Modpack のダウンロード総量は 8GB までです' });
  });
export type ImportIndex = z.infer<typeof ImportIndexSchema>;
export const ModpackIdentitySchema = z.object({
  source: z.enum(['modrinth', 'curseforge', 'upload']),
  projectId: z.string().optional(),
  versionId: z.string(),
  name: z.string(),
  versionNumber: z.string(),
});
export const PackOverrideSchema = z.object({
  sha512: sha512.optional(),
  path: PackPathSchema,
  data: z
    .string()
    .max((MODPACK_LIMITS.expandedBytes * 4) / 3 + 4)
    .default(''),
  url: z.string().url().optional(),
  size: z.number().int().nonnegative().max(MODPACK_LIMITS.expandedBytes).optional(),
  sha1,
});
export const LauncherPackFileSchema = z.object({
  path: PackPathSchema,
  url: PackDownloadUrlSchema,
  sha1,
  sha512: sha512.optional(),
  size: z.number().int().nonnegative(),
  side: z.enum(['required', 'optional']),
});
export const PackOverridesManifestSchema = z.object({
  url: z.string().url(),
  sha1,
  size: z.number().int().nonnegative(),
  files: z.array(z.object({ path: PackPathSchema, sha1 })),
});
export const ModpackDeploymentSchema = z.object({
  importId: z.string().uuid().optional(),
  missing: z.array(InstalledModSchema).optional(),
  identity: ModpackIdentitySchema,
  software: SoftwareSpecSchema,
  files: z.array(PackFileSchema).max(MODPACK_LIMITS.files),
  serverOverrides: z.array(PackOverrideSchema).max(MODPACK_LIMITS.files),
  clientOverrides: z.array(PackOverrideSchema).max(MODPACK_LIMITS.files),
});
export type ModpackDeployment = z.infer<typeof ModpackDeploymentSchema>;
export type PackOverride = z.infer<typeof PackOverrideSchema>;
export type PackFile = z.infer<typeof PackFileSchema>;
export const ModpackSelectionSchema = z
  .object({
    source: z.enum(['modrinth', 'curseforge']).default('modrinth'),
    projectId: z.string().min(1).max(100),
    versionId: z.string().min(1).max(100),
  })
  .strict();
export const ModpackSearchSchema = z
  .object({
    source: z.enum(['modrinth', 'curseforge']).default('modrinth'),
    q: z.string().max(100).default(''),
    offset: z.coerce.number().int().min(0).max(1000).default(0),
  })
  .strict();
export const ModpackIdSchema = z.string().uuid();
export const ChangeModpackSchema = z.object({ modpackId: ModpackIdSchema, confirmReplace: z.literal(true) }).strict();
export const ModpackPreviewSchema = z.object({
  id: ModpackIdSchema,
  identity: ModpackIdentitySchema,
  software: SoftwareSpecSchema,
  missing: z.array(InstalledModSchema).optional(),
  modCount: z.number(),
  serverModCount: z.number(),
  recommendedMemoryMb: z.number(),
});
export type ModpackPreview = z.infer<typeof ModpackPreviewSchema>;
export function modpackSoftware(index: ImportIndex) {
  const dependencies = index.dependencies;
  const unsupported = Object.keys(dependencies).filter(
    (key) => !['minecraft', 'fabric-loader', 'neoforge', 'forge'].includes(key),
  );
  if (unsupported.length) throw new Error(`対応していないローダー・依存です: ${unsupported.join(', ')}`);
  const loaders = (['fabric-loader', 'neoforge', 'forge'] as const).filter((key) => dependencies[key]);
  if (loaders.length !== 1 || !dependencies.minecraft) throw new Error('Minecraft とローダーを一意に決められません');
  const key = loaders[0]!;
  return SoftwareSpecSchema.parse({
    family: key === 'fabric-loader' ? 'fabric' : key,
    mcVersion: dependencies.minecraft,
    loaderVersion: dependencies[key],
  });
}
export function clientPackPath(value: string) {
  return (
    PackPathSchema.safeParse(value).success &&
    !/^(mods|saves|logs|crash-reports)(\/|$)|^servers\.dat(\/|$)/i.test(value)
  );
}
export function serverPackPath(value: string) {
  return (
    PackPathSchema.safeParse(value).success &&
    !/^(world[^/]*|saves|logs|crash-reports|plugins|libraries|versions)(\/|$)|^(server\.properties|eula\.txt|ops\.json|whitelist\.json|banned-.*\.json|usercache\.json|start\.[^/]+)$/i.test(
      value,
    )
  );
}
export function launcherPackFiles(pack: ModpackDeployment) {
  return pack.files
    .filter((file) => file.env.client !== 'unsupported' && clientPackPath(file.path))
    .map((file) => ({
      path: file.path,
      url: file.downloads[0]!,
      ...file.hashes,
      size: file.fileSize,
      side: file.env.client as 'required' | 'optional',
    }));
}

export const CurseForgeManifestSchema = z
  .object({
    manifestType: z.literal('minecraftModpack'),
    manifestVersion: z.literal(1),
    name: z.string().min(1).max(200),
    version: z.string().min(1).max(100),
    minecraft: z.object({
      version: z.string().min(1).max(100),
      modLoaders: z.array(z.object({ id: z.string().min(1).max(100), primary: z.boolean() })).min(1),
    }),
    files: z
      .array(
        z.object({
          projectID: z.number().int().positive(),
          fileID: z.number().int().positive(),
          required: z.boolean(),
        }),
      )
      .max(MODPACK_LIMITS.files),
    overrides: z
      .string()
      .transform((value) => (value.endsWith('/') ? value.slice(0, -1) : value))
      .pipe(PackPathSchema)
      .default('overrides'),
  })
  .superRefine((manifest, ctx) => {
    if (new Set(manifest.files.map((file) => file.projectID)).size !== manifest.files.length)
      ctx.addIssue({ code: 'custom', message: 'Mod のプロジェクトが重複しています' });
  });
export function curseforgeModpackSoftware(manifest: z.infer<typeof CurseForgeManifestSchema>) {
  const primary = manifest.minecraft.modLoaders.filter((loader) => loader.primary);
  if (primary.length !== 1) throw new Error('primary ローダーを一意に決められません');
  const match = /^(forge|neoforge|fabric)-(.+)$/.exec(primary[0]!.id);
  if (!match) throw new Error('対応していないローダーです');
  return SoftwareSpecSchema.parse({ family: match[1], mcVersion: manifest.minecraft.version, loaderVersion: match[2] });
}

export const ModpackJarParamsSchema = z.object({ id: ModpackIdSchema, projectId: z.string().regex(/^[1-9][0-9]*$/) });
